import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TextareaModule } from 'primeng/textarea';
import { filter, Subject, take, takeUntil, timer } from 'rxjs';
import { ChatWindowService } from 'src/app/shared/services/chat-window.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { TextInputValidate } from '../../../shared/directives/text-input-validate.directive';
import { User } from '../../../shared/models/user.model';
import { ChatBoxComponent } from '../chat-box/chat-box.component';

@Component({
  selector: 'app-chat-live-support',
  templateUrl: './chat-live-support.component.html',
  styleUrls: ['./chat-live-support.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChatBoxComponent,
    FormsModule,
    TextareaModule,
    TextInputValidate,
    AsyncPipe,
    SvgIconComponent,
  ],
})
export class ChatLiveSupportComponent implements OnInit, OnDestroy {
  @Output() emitTotalUnread = new EventEmitter<number>();

  readonly currentStep$ = this.chatWindowService.currentChatStepSubject$;

  newMessageText = '';
  currentUser!: User;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly chatWindowService: ChatWindowService,
    private readonly userInfoService: UserInfoService
  ) {}

  ngOnInit() {
    this.connectAgent();
    this.loadCurrentUser();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSend() {
    this.chatWindowService.setCurrentStep('SENT_AGENT');
  }

  /**
   * Handle Enter key press in textarea
   * - Enter: Send message
   * - Shift+Enter: Allow new line
   */
  handleEnterKey(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      this.onSend();
    }
  }

  showForm(step: AppConstant.Step) {
    this.chatWindowService.setCurrentStep(step);
  }

  emitUnreadMsg(count: number) {
    this.emitTotalUnread.emit(count);
  }

  /**
   * Load current user from user service
   */
  private loadCurrentUser() {
    this.userInfoService.userSubject
      .pipe(
        filter((userObject: User) => !!userObject?.id),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(user => {
        this.currentUser = user;
      });
  }

  /**
   * Initiate agent connection with waiting state
   * Transitions to CHAT state after 1 second delay
   */
  private connectAgent() {
    this.chatWindowService.setCurrentChatStep('WAIT');

    timer(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.chatWindowService.setCurrentChatStep('CHAT');
      });
  }
}
